import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { useResearcherSignature } from '../../../hooks/useResearcherSignature';
import { convertBlobToBase64 } from '../utils/imageUtils';
import { downloadPDF } from '../utils/pdfDownload';

interface PDFDownloadButtonWithSignatureProps {
  /** Function that receives signatureBase64 (or null) and returns the PDF document */
  children: (signatureBase64: string | null) => React.ReactElement;
  /** PDF filename (without .pdf extension) */
  filename: string;
  /** Investigation UUID for signature fetching */
  uuidInvestigation: string;
  /** Researcher UUID whose signature to fetch (optional) */
  uuidResearcher?: string;
  /** Button text/content when ready */
  label?: React.ReactNode;
  /** Button variant */
  variant?: 'text' | 'outlined' | 'contained';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Additional button styles */
  style?: React.CSSProperties;
  /** Button color */
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

/**
 * PDF Download Button with Signature Fetching
 * 
 * Automatically fetches researcher signature from API and converts it to base64
 * before enabling PDF download. Shows loading state while fetching and converting.
 * 
 * @example
 * ```tsx
 * <PDFDownloadButtonWithSignature
 *   uuidInvestigation="inv-123"
 *   uuidResearcher="researcher-456"
 *   filename="submission-report"
 *   label="Download PDF"
 * >
 *   {(signatureBase64) => (
 *     <SubmissionPDFDocument 
 *       {...props}
 *       signatureBase64={signatureBase64}
 *     />
 *   )}
 * </PDFDownloadButtonWithSignature>
 * ```
 */
export const PDFDownloadButtonWithSignature: React.FC<PDFDownloadButtonWithSignatureProps> = ({
  children,
  filename,
  uuidInvestigation,
  uuidResearcher,
  label = 'Download PDF',
  variant = 'contained',
  size = 'medium',
  style,
  color = 'primary',
}) => {
  // Fetch signature URL from API
  const { signatureUrl, loading: loadingSignature, error } = useResearcherSignature({
    uuidInvestigation,
    uuidResearcher: uuidResearcher || '',
    enabled: !!uuidResearcher && !!uuidInvestigation,
  });

  // State for base64-converted signature (React PDF needs base64, not URLs)
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [convertingImage, setConvertingImage] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);

  // SHER-208 — these MUST stay above the `loadingSignature || convertingImage`
  // early return below. They were originally declared after it (SHER-197,
  // fd117215), which meant the first render (signature still loading) skipped
  // them and the post-load render called two extra hooks → React threw
  // "Rendered more hooks than during the previous render", surfaced by the
  // records ErrorBoundary as "Something went wrong" on every clinical PDF.
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Convert signature URL to base64 when it's available
  useEffect(() => {
    if (signatureUrl) {
      // Check if it's already base64 (starts with "data:")
      if (signatureUrl.startsWith('data:')) {
        setSignatureBase64(signatureUrl);
        setConversionError(null);
        setConvertingImage(false);
      } else {
        // It's a URL - need to convert to base64
        setConvertingImage(true);
        setConversionError(null);
        
        convertBlobToBase64(signatureUrl)
          .then(base64 => {
            setSignatureBase64(base64);
            setConversionError(null);
          })
          .catch(err => {
            const errorMsg = 'Failed to load signature image. The PDF will generate without signature.';
            setSignatureBase64(null);
            setConversionError(errorMsg);
          })
          .finally(() => {
            setConvertingImage(false);
          });
      }
    } else {
      setSignatureBase64(null);
      setConversionError(null);
    }
  }, [signatureUrl]);

  // Show loading state while signature is being fetched OR converted
  if (loadingSignature || convertingImage) {
    return (
      <Button 
        variant={variant} 
        size={size} 
        disabled 
        style={style}
        color={color}
      >
        <CircularProgress size={20} style={{ marginRight: 8 }} />
        {loadingSignature ? 'Loading signature...' : 'Converting image...'}
      </Button>
    );
  }

  // SHER-197 — open the PDF inline in a new browser tab instead of
  // forcing a download. The original implementation wrapped the button
  // in @react-pdf/renderer's <PDFDownloadLink>, which renders
  //   <a href="blob:…" download="filename.pdf">
  // and triggers a download by design — silently dropping every clinical
  // PDF into the user's Downloads folder on shared hospital machines.
  // Pedro re-flagged this on 2026-05-16 ("se siguen bajando a local");
  // the original SHER-197 fix (commit 7d5b7721, 2026-05-06) only
  // patched the BILLING flow's `pdfDownload.ts:downloadPDF`, which uses
  //   window.open(URL.createObjectURL(blob), '_blank')
  // — this component now uses that same helper so the clinical /
  // submission flow has the same inline-preview behaviour. The
  // browser's native PDF viewer still exposes its own Download button,
  // so users can save explicitly when they want to.
  const handleOpen = async () => {
    if (generatingPDF) return;
    setGeneratingPDF(true);
    setGenerationError(null);
    try {
      await downloadPDF(children(signatureBase64), filename);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to open PDF';
      setGenerationError(errorMsg);
    } finally {
      setGeneratingPDF(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleOpen}
        disabled={generatingPDF}
        color={color}
        style={style}
      >
        {generatingPDF ? (
          <>
            <CircularProgress size={20} style={{ marginRight: 8 }} />
            Preparing PDF...
          </>
        ) : (
          label
        )}
      </Button>

      {/* Show warning if signature conversion failed */}
      {conversionError && !loadingSignature && !convertingImage && (
        <Box sx={{ mt: 1, color: 'warning.main', fontSize: '0.875rem', textAlign: 'center' }}>
          ⚠️ {conversionError}
        </Box>
      )}
      {generationError && (
        <Box sx={{ mt: 1, color: 'error.main', fontSize: '0.875rem', textAlign: 'center' }}>
          ⚠️ {generationError}
        </Box>
      )}
    </>
  );
};

export default PDFDownloadButtonWithSignature;

